/* BOA Ideas: Mobile Quick Buy
 * Bottom drawer quick buy for mobile (≤767px)
 * Coexists with the existing desktop inline quickbuy
 */
/* global addVariantToCart */

(function () {
	'use strict';

	const MOBILE_MAX_WIDTH = 767;
	const THUMB_WIDTH_1X = 400;
	const THUMB_WIDTH_2X = 800;
	const MAX_GALLERY_IMAGES = 8;
	const ATC_SUCCESS_DELAY_MS = 2000;

	const SIZE_OPTION_NAMES = ['מידה', 'Size', 'size', 'SIZE', 'Размер'];
	const COLOR_OPTION_NAMES = ['צבע', 'Color', 'color', 'COLOR', 'Colour'];

	const TEXT = {
		addToCart: 'הוסף לסל',
		selectSize: 'בחר/י מידה',
		outOfStock: 'אזל מהמלאי',
		adding: 'מוסיף...',
		addedToCart: 'נוסף לסל',
		goToProduct: 'לעמוד המוצר',
		color: 'צבע',
	};

	const MQB_SHELL_TEMPLATE = (() => {
		const template = document.createElement('template');
		template.innerHTML = '<div class="mqb-overlay" aria-hidden="true"></div>'
			+ '<div class="mqb-drawer" role="dialog" aria-modal="true" dir="rtl">'
			+ '<div class="mqb-drawer__handle-wrap"><div class="mqb-drawer__handle"></div></div>'
			+ '<div class="mqb-drawer__gallery"></div>'
			+ '<div class="mqb-drawer__content"></div>'
			+ '</div>';
		return template;
	})();

	const MobileQuickBuy = {
		drawer: null,
		overlay: null,
		currentProduct: null,
		selectedOptions: {},
		touchStartY: 0,
		touchCurrentY: 0,
		isTouching: false,

		init() {
			this._setup();
		},

		_setup() {
			this._createDrawer();
			this._bindEvents();
		},

		_createDrawer() {
			const shell = MQB_SHELL_TEMPLATE.content.cloneNode(true);
			this.overlay = shell.querySelector('.mqb-overlay');
			this.drawer = shell.querySelector('.mqb-drawer');
			this.drawer.setAttribute('aria-label', TEXT.addToCart);
			document.body.appendChild(shell);
		},

		// eslint-disable-next-line max-lines-per-function
		_bindEvents() {
			document.addEventListener('click', (event) => {
				const trigger = event.target.closest('[data-mqb-trigger]');
				if (!trigger) {
					return;
				}
				if (window.innerWidth > MOBILE_MAX_WIDTH) {
					return;
				}
				event.preventDefault();
				event.stopPropagation();
				this._handleIconClick(trigger);
			});

			this.overlay.addEventListener('click', () => {
				this.closeDrawer();
			});

			document.addEventListener('keydown', (event) => {
				if (event.key === 'Escape' && this.drawer && this.drawer.classList.contains('mqb-drawer--open')) {
					this.closeDrawer();
				}
			});

			const handle = this.drawer.querySelector('.mqb-drawer__handle-wrap');

			handle.addEventListener('touchstart', (event) => {
				this.touchStartY = event.touches[0].clientY;
				this.touchCurrentY = this.touchStartY;
				this.isTouching = true;
			}, { passive: true });

			handle.addEventListener('touchmove', (event) => {
				if (!this.isTouching) {
					return;
				}
				this.touchCurrentY = event.touches[0].clientY;
				const delta = this.touchCurrentY - this.touchStartY;
				if (delta > 0) {
					this.drawer.style.transform = `translateY(${delta}px)`;
					this.drawer.style.transition = 'none';
				}
			}, { passive: true });

			const finishDrawerTouch = () => {
				if (!this.isTouching) {
					return;
				}
				this.isTouching = false;
				const delta = this.touchCurrentY - this.touchStartY;
				this.drawer.style.transition = '';
				if (delta > 0) {
					this.closeDrawer();
				} else {
					this.drawer.style.transform = '';
				}
			};

			handle.addEventListener('touchend', finishDrawerTouch);
			handle.addEventListener('touchcancel', finishDrawerTouch);

			// eslint-disable-next-line complexity
			this.drawer.addEventListener('click', (event) => {
				const colorThumb = event.target.closest('.mqb-color-thumb');
				if (colorThumb) {
					const handle = colorThumb.dataset.handle;
					const url = colorThumb.dataset.url;
					if (handle && this.currentProduct && handle !== this.currentProduct.handle) {
						this._switchToProduct(handle, url, colorThumb);
					}
					return;
				}

				const pill = event.target.closest('.mqb-pill:not(.mqb-pill--unavailable)');
				if (pill) {
					this._handleOptionSelect(pill);
					return;
				}

				const swatch = event.target.closest('.mqb-swatch:not(.mqb-swatch--unavailable)');
				if (swatch) {
					this._handleOptionSelect(swatch);
					return;
				}

				const atcButton = event.target.closest('.mqb-drawer__atc:not(:disabled)');
				if (atcButton) {
					this._handleAddToCart(atcButton);
				}
			});
		},

		// eslint-disable-next-line complexity
		_handleIconClick(trigger) {
			const productBlock = trigger.closest('.product-block');
			if (!productBlock) {
				return;
			}

			const productData = this._getProductData(productBlock);
			if (!productData) {
				return;
			}

			const productLink = productBlock.querySelector('.product-link');
			if (productLink) {
				const linkHandlePart = productLink.href.split('/products/')[1];
				if (linkHandlePart) {
					const linkHandle = linkHandlePart.split('?')[0].split('/')[0];
					if (linkHandle !== productData.handle) {
						this._fetchAndOpen(trigger, linkHandle, productLink.href, productData.related_colors);
						return;
					}
				}
			}

			this.openDrawer(productData);
		},

		_getProductData(productBlock) {
			const scriptTag = productBlock.querySelector('.mqb-product-data');
			if (!scriptTag) {
				return null;
			}
			try {
				return JSON.parse(scriptTag.textContent);
			} catch (error) {
				console.error('MQB: failed to parse product data', error);
				return null;
			}
		},

		_loadProduct(handle, productUrl) {
			return fetch(`/products/${handle}.json`)
				.then((response) => {
					if (!response.ok) {
						throw new Error('MQB: product fetch failed');
					}
					return response.json();
				})
				.then((data) => {
					return this._transformApiProduct(data.product, productUrl);
				});
		},

		_fetchAndOpen(trigger, handle, productUrl, existingRelatedColors) {
			trigger.classList.add('mqb-icon--loading');
			this._loadProduct(handle, productUrl)
				.then((productData) => {
					trigger.classList.remove('mqb-icon--loading');
					productData.related_colors = existingRelatedColors || [];
					this.openDrawer(productData);
				})
				.catch(() => {
					trigger.classList.remove('mqb-icon--loading');
					if (productUrl) {
						window.location.assign(productUrl);
					}
				});
		},

		_switchToProduct(handle, url, swatchElement) {
			const relatedColors = this.currentProduct.related_colors;

			this._loadProduct(handle, url)
				.then((productData) => {
					productData.related_colors = relatedColors;
					this.currentProduct = productData;
					this.selectedOptions = {};

					const colorThumbs = this.drawer.querySelectorAll('.mqb-color-thumb');
					colorThumbs.forEach((thumb) => {
						thumb.classList.remove('mqb-color-thumb--selected');
					});
					if (swatchElement) {
						swatchElement.classList.add('mqb-color-thumb--selected');
					}

					this._renderGallery(productData);
					this._renderContent(productData);
				})
				.catch((error) => {
					console.error('MQB: failed to switch product', error);
				});
		},

		_getThumbImageUrl(url, width = THUMB_WIDTH_2X) {
			if (!url) {
				return url;
			}
			if (/[?&]width=\d+/.test(url)) {
				return url.replace(/([?&])width=\d+/, `$1width=${width}`);
			}
			const separator = url.indexOf('?') === -1 ? '?' : '&';
			return `${url}${separator}width=${width}`;
		},

		_transformApiProduct(product, productUrl) {
			return {
				id: product.id,
				title: product.title,
				handle: product.handle,
				url: productUrl,
				images: (product.images || []).slice(0, MAX_GALLERY_IMAGES).map((image) => {
					return this._getThumbImageUrl(image.src, THUMB_WIDTH_2X);
				}),
				options_with_values: product.options.map((option) => {
					return { name: option.name, position: option.position, values: option.values };
				}),
				variants: (product.variants || []).map((variant) => {
					return {
						id: variant.id,
						available: variant.available,
						options: [variant.option1, variant.option2, variant.option3],
					};
				}),
				related_colors: [],
			};
		},

		_getDrawerOptions(product) {
			const hasRelatedColors = product.related_colors && product.related_colors.length > 1;
			let sizeOption = null;
			let colorOption = null;

			(product.options_with_values || []).forEach((option) => {
				if (SIZE_OPTION_NAMES.includes(option.name)) {
					sizeOption = option;
				}
				if (!hasRelatedColors && COLOR_OPTION_NAMES.includes(option.name)) {
					colorOption = option;
				}
			});

			return { hasRelatedColors, sizeOption, colorOption };
		},

		_getVariantOption(variant, position) {
			const value = variant.options && variant.options[position - 1];
			if (value != null && value !== '') {
				return String(value).trim();
			}
			return null;
		},

		_optionValueName(value) {
			if (value != null && typeof value === 'object' && value.name != null) {
				return String(value.name);
			}
			return String(value);
		},

		_optionValuesMatch(first, second) {
			if (first == null || second == null) {
				return false;
			}
			return String(first).trim() === String(second).trim();
		},

		_getEffectiveOptionValue(option) {
			if (!option) {
				return undefined;
			}
			if (this.selectedOptions[option.position] !== undefined) {
				return this.selectedOptions[option.position];
			}
			if (option.values && option.values.length === 1) {
				return option.values[0];
			}
			return undefined;
		},

		_autoSelectSingleValueOptions(product, content) {
			if (this._getDrawerOptions(product).hasRelatedColors) {
				return;
			}

			(product.options_with_values || []).forEach((option) => {
				if (option.values.length !== 1) {
					return;
				}
				if (COLOR_OPTION_NAMES.includes(option.name)) {
					this.selectedOptions[option.position] = option.values[0];
					const swatch = content.querySelector(`.mqb-swatch[data-option-position="${option.position}"]`);
					if (swatch) {
						swatch.classList.add('mqb-swatch--selected');
					}
				}
			});
		},

		_isVariantAvailable(variant) {
			return variant.available !== false;
		},

		_parseSizesAvailability(raw) {
			if (!raw) {
				return null;
			}
			const availabilityMap = {};
			raw.split(',').forEach((entry) => {
				const parts = entry.split(':');
				if (parts[0]) {
					availabilityMap[String(parts[0]).trim()] = parts[1] === 'true';
				}
			});
			return availabilityMap;
		},

		_getRelatedColorSizesAvailability(product) {
			if (!product.related_colors || product.related_colors.length <= 1) {
				return null;
			}
			const entry = product.related_colors.find((color) => {
				return color.handle === product.handle;
			});
			if (entry && entry.sizes_availability) {
				return this._parseSizesAvailability(entry.sizes_availability);
			}
			return null;
		},

		_isSizeValueAvailable(product, sizeOption, sizeValue, colorValue, colorOption) {
			const sizesAvailability = this._getRelatedColorSizesAvailability(product);
			if (sizesAvailability) {
				return sizesAvailability[String(sizeValue).trim()] === true;
			}

			return product.variants.some((variant) => {
				if (colorOption && colorValue != null && !this._optionValuesMatch(this._getVariantOption(variant, colorOption.position), colorValue)) {
					return false;
				}
				return this._optionValuesMatch(this._getVariantOption(variant, sizeOption.position), sizeValue) &&
					this._isVariantAvailable(variant);
			});
		},

		openDrawer(productData) {
			this.currentProduct = productData;
			this.selectedOptions = {};
			this._populateDrawer(productData);
			this.overlay.classList.add('mqb-overlay--visible');
			this.drawer.classList.add('mqb-drawer--open');
			this.drawer.style.transform = '';
			document.body.classList.add('mqb-open');
		},

		closeDrawer() {
			this.drawer.style.transform = '';
			this.drawer.style.transition = '';
			this.overlay.classList.remove('mqb-overlay--visible');
			this.drawer.classList.remove('mqb-drawer--open');
			document.body.classList.remove('mqb-open');
			this.currentProduct = null;
			this.selectedOptions = {};
		},

		_populateDrawer(product) {
			this._renderGallery(product);
			this._renderContent(product);
		},

		_renderGallery(product) {
			const gallery = this.drawer.querySelector('.mqb-drawer__gallery');
			let html = '';

			html += '<div class="mqb-drawer__header">';
			html += `<a class="mqb-drawer__page-link" href="${this._escape(product.url)}">`;
			html += '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>';
			html += `<span>${this._escape(TEXT.goToProduct)}</span>`;
			html += '</a>';
			html += '</div>';

			html += '<div class="mqb-drawer__images">';
			(product.images || []).forEach((imageUrl) => {
				const thumb1x = this._getThumbImageUrl(imageUrl, THUMB_WIDTH_1X);
				const thumb2x = this._getThumbImageUrl(imageUrl, THUMB_WIDTH_2X);
				html += `<img class="mqb-drawer__thumb" src="${thumb2x}" srcset="${thumb1x} 400w, ${thumb2x} 800w" sizes="42vw" alt="" loading="lazy" decoding="async">`;
			});
			html += '</div>';
			gallery.innerHTML = html;
		},

		// eslint-disable-next-line max-lines-per-function, complexity
		_renderContent(product) {
			const content = this.drawer.querySelector('.mqb-drawer__content');
			const drawerOptions = this._getDrawerOptions(product);
			const { sizeOption, colorOption } = drawerOptions;
			let html = '';

			if (drawerOptions.hasRelatedColors) {
				html += `<div class="mqb-drawer__option-label">${this._escape(TEXT.color)}</div>`;
				html += '<div class="mqb-color-swatches">';
				product.related_colors.forEach((color) => {
					if (!color.image) {
						return;
					}
					const isSelected = color.handle === product.handle;
					html += `<img class="mqb-color-thumb${isSelected ? ' mqb-color-thumb--selected' : ''}" src="${this._escape(color.image)}" alt="${this._escape(color.handle)}" data-handle="${this._escape(color.handle)}" data-url="${this._escape(color.url)}" loading="lazy">`;
				});
				html += '</div>';
			} else if (colorOption) {
				html += `<div class="mqb-drawer__option-label">${this._escape(colorOption.name)}</div>`;
				html += `<div class="mqb-drawer__options" data-option-name="${this._escape(colorOption.name)}" data-option-position="${colorOption.position}">`;
				colorOption.values.forEach((value) => {
					const colorName = this._optionValueName(value);
					const hasAvailable = product.variants.some((variant) => {
						return this._optionValuesMatch(this._getVariantOption(variant, colorOption.position), colorName) &&
							this._isVariantAvailable(variant);
					});
					const swatchStyle = `background-color:${colorName.toLowerCase().replace(/\s+/g, '')}`;
					html += `<button class="mqb-swatch${hasAvailable ? '' : ' mqb-swatch--unavailable'}" data-option-value="${this._escape(colorName)}" data-option-position="${colorOption.position}" style="${this._escape(swatchStyle)}" title="${this._escape(colorName)}" type="button"></button>`;
				});
				html += '</div>';
			}

			if (sizeOption) {
				html += `<div class="mqb-drawer__option-label">${this._escape(sizeOption.name)}</div>`;
				html += `<div class="mqb-drawer__options" data-option-name="${this._escape(sizeOption.name)}" data-option-position="${sizeOption.position}">`;
				sizeOption.values.forEach((value) => {
					const sizeName = this._optionValueName(value);
					const isAvailable = this._isSizeValueAvailable(product, sizeOption, sizeName, null, null);
					html += `<button class="mqb-pill${isAvailable ? '' : ' mqb-pill--unavailable'}" data-option-value="${this._escape(sizeName)}" data-option-position="${sizeOption.position}" type="button">${this._escape(sizeName)}</button>`;
				});
				html += '</div>';
			}

			const buttonDisabled = sizeOption ? 'disabled' : '';
			const buttonText = sizeOption ? TEXT.selectSize : TEXT.addToCart;
			html += `<button class="mqb-drawer__atc" type="button" ${buttonDisabled}>${buttonText}</button>`;

			content.innerHTML = html;

			this._autoSelectSingleValueOptions(product, content);

			if (!sizeOption && !colorOption) {
				const atcButton = content.querySelector('.mqb-drawer__atc');
				const firstAvailable = product.variants.find((variant) => {
					return this._isVariantAvailable(variant);
				});
				if (atcButton && firstAvailable) {
					atcButton.removeAttribute('disabled');
					atcButton.dataset.variantId = String(firstAvailable.id);
					atcButton.textContent = TEXT.addToCart;
				}
			} else {
				this._updateAtcButton();
			}
		},

		// eslint-disable-next-line complexity
		_handleOptionSelect(element) {
			const position = parseInt(element.getAttribute('data-option-position'), 10);
			const value = element.getAttribute('data-option-value');
			const optionsGroup = element.closest('.mqb-drawer__options');
			const optionItems = optionsGroup.querySelectorAll('.mqb-pill, .mqb-swatch');

			optionItems.forEach((item) => {
				item.classList.remove('mqb-pill--selected', 'mqb-swatch--selected');
			});
			element.classList.add(element.classList.contains('mqb-pill') ? 'mqb-pill--selected' : 'mqb-swatch--selected');

			this.selectedOptions[position] = value;

			const drawerOptions = this._getDrawerOptions(this.currentProduct);
			if (!drawerOptions.hasRelatedColors && drawerOptions.colorOption && position === drawerOptions.colorOption.position && drawerOptions.sizeOption) {
				this._updateSizeAvailabilityForColor(value, drawerOptions.colorOption.position, drawerOptions.sizeOption.position);
			}

			this._updateAtcButton();
		},

		_updateSizeAvailabilityForColor(colorValue, colorPosition, sizePosition) {
			const product = this.currentProduct;
			const colorOption = { position: colorPosition };
			const sizeOption = { position: sizePosition };
			const sizePills = this.drawer.querySelectorAll('.mqb-pill');

			sizePills.forEach((pill) => {
				const sizeValue = pill.getAttribute('data-option-value');
				const isAvailable = this._isSizeValueAvailable(product, sizeOption, sizeValue, colorValue, colorOption);

				if (isAvailable) {
					pill.classList.remove('mqb-pill--unavailable');
				} else {
					pill.classList.add('mqb-pill--unavailable');
					pill.classList.remove('mqb-pill--selected');
				}
			});
			delete this.selectedOptions[sizePosition];
		},

		// eslint-disable-next-line complexity
		_updateAtcButton() {
			const product = this.currentProduct;
			const atcButton = this.drawer.querySelector('.mqb-drawer__atc');
			if (!atcButton) {
				return;
			}

			const drawerOptions = this._getDrawerOptions(product);
			const selectedSize = this._getEffectiveOptionValue(drawerOptions.sizeOption);
			const selectedColor = this._getEffectiveOptionValue(drawerOptions.colorOption);

			if (drawerOptions.sizeOption && selectedSize === undefined) {
				atcButton.setAttribute('disabled', 'disabled');
				atcButton.textContent = TEXT.selectSize;
				delete atcButton.dataset.variantId;
				return;
			}

			const matchedVariant = product.variants.find((variant) => {
				if (drawerOptions.sizeOption && !this._optionValuesMatch(this._getVariantOption(variant, drawerOptions.sizeOption.position), selectedSize)) {
					return false;
				}
				if (drawerOptions.colorOption && !this._optionValuesMatch(this._getVariantOption(variant, drawerOptions.colorOption.position), selectedColor)) {
					return false;
				}
				return true;
			});

			if (matchedVariant && this._isVariantAvailable(matchedVariant)) {
				atcButton.removeAttribute('disabled');
				atcButton.textContent = TEXT.addToCart;
				atcButton.dataset.variantId = String(matchedVariant.id);
			} else {
				atcButton.setAttribute('disabled', 'disabled');
				atcButton.textContent = TEXT.outOfStock;
				delete atcButton.dataset.variantId;
			}
		},

		_handleAddToCart(atcButton) {
			const variantId = atcButton.dataset.variantId;
			if (!variantId) {
				return;
			}
			if (typeof addVariantToCart !== 'function') {
				console.error('MQB: addVariantToCart missing');
				return;
			}

			atcButton.setAttribute('disabled', 'disabled');
			atcButton.textContent = TEXT.adding;

			addVariantToCart(parseInt(variantId, 10), 1, true).then((result) => {
				if (result && result.success) {
					atcButton.classList.add('mqb-drawer__atc--success');
					atcButton.innerHTML = `<span class="mqb-drawer__atc-label"><span class="mqb-drawer__atc-text">${this._escape(TEXT.addedToCart)}</span><svg class="mqb-drawer__atc-check" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg></span>`;
					setTimeout(() => {
						this.closeDrawer();
					}, ATC_SUCCESS_DELAY_MS);
				} else {
					atcButton.removeAttribute('disabled');
					atcButton.textContent = TEXT.addToCart;
				}
			});
		},

		_escape(string) {
			return String(string)
				.replace(/&/g, '&amp;')
				.replace(/"/g, '&quot;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;');
		},
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => {
			MobileQuickBuy.init();
		});
	} else {
		MobileQuickBuy.init();
	}
})();
